import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as artifactsApi from '../api/artifacts';

export function Artifacts() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canView = hasPermission('assets:read');
  const [artifacts, setArtifacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    search: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canView) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const activeFilters = { limit: 50 };
      if (filters.category) activeFilters.category = filters.category;
      if (filters.search) activeFilters.search = filters.search;
      
      const [result, cats] = await Promise.all([
        artifactsApi.getArtifacts(selectedOrg.id, activeFilters),
        artifactsApi.getCategories(selectedOrg.id)
      ]);
      setArtifacts(result.artifacts);
      setTotal(result.total);
      setCategories(cats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canView, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = async (artifactId) => {
    if (!selectedOrg) return;
    try {
      const artifact = await artifactsApi.getArtifact(selectedOrg.id, artifactId);
      setSelectedArtifact(artifact);
      setShowDrawer(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async (artifactId, e) => {
    if (e) e.stopPropagation();
    if (!selectedOrg || downloading) return;
    
    setDownloading(artifactId);
    try {
      const signedUrl = await artifactsApi.getDownloadUrl(selectedOrg.id, artifactId);
      window.open(signedUrl.url, '_blank');
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloading(null);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getCategoryColor = (category) => {
    const colors = {
      compliance: { bg: '#c6f6d5', color: '#276749' },
      report: { bg: '#bee3f8', color: '#2b6cb0' },
      certificate: { bg: '#faf089', color: '#975a16' },
      form: { bg: '#e9d8fd', color: '#6b46c1' },
      other: { bg: '#e2e8f0', color: '#4a5568' }
    };
    return colors[category] || colors.other;
  };

  if (!canView) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need asset read permissions to view artifacts.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Artifact Registry"
        subtitle="Securely download compliance documents and reports"
      />

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Category</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            style={styles.filterSelect}
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
            ))}
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search</label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            placeholder="Search artifacts..."
            style={styles.filterInput}
            data-testid="search-input"
          />
        </div>
        <div style={styles.totalCount}>
          {total} artifact{total !== 1 ? 's' : ''}
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading artifacts...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="artifacts-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Size</th>
                <th style={styles.th}>Uploaded</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artifacts.map(artifact => {
                const catStyle = getCategoryColor(artifact.category);
                return (
                  <tr
                    key={artifact.id}
                    style={styles.tr}
                    onClick={() => handleRowClick(artifact.id)}
                    data-testid={`artifact-${artifact.id}`}
                  >
                    <td style={styles.td}>
                      <div style={styles.nameCell}>
                        <span style={styles.artifactName}>{artifact.name}</span>
                        <span style={styles.filename}>{artifact.filename}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: catStyle.bg, color: catStyle.color }}>
                        {artifact.category}
                      </span>
                    </td>
                    <td style={styles.td}>{formatSize(artifact.size)}</td>
                    <td style={styles.td}>{new Date(artifact.uploadedAt).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <button
                        onClick={(e) => handleDownload(artifact.id, e)}
                        disabled={downloading === artifact.id}
                        style={styles.downloadButton}
                        data-testid={`download-${artifact.id}`}
                      >
                        {downloading === artifact.id ? 'Preparing...' : 'Download'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {artifacts.length === 0 && (
                <tr>
                  <td colSpan="5" style={styles.emptyState}>No artifacts found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDrawer && selectedArtifact && (
        <div style={styles.drawerOverlay} onClick={() => setShowDrawer(false)}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()} data-testid="artifact-drawer">
            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerTitle}>Artifact Details</h3>
              <button onClick={() => setShowDrawer(false)} style={styles.closeButton}>×</button>
            </div>
            
            <div style={styles.drawerContent}>
              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>{selectedArtifact.name}</h4>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Filename</span>
                  <span>{selectedArtifact.filename}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Category</span>
                  <span style={{ ...styles.badge, ...getCategoryColor(selectedArtifact.category) }}>
                    {selectedArtifact.category}
                  </span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Size</span>
                  <span>{formatSize(selectedArtifact.size)}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>MIME Type</span>
                  <span>{selectedArtifact.mimeType}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Uploaded</span>
                  <span>{new Date(selectedArtifact.uploadedAt).toLocaleString()}</span>
                </div>
              </div>

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Integrity</h4>
                <div style={styles.checksumBox} data-testid="checksum">
                  <code>{selectedArtifact.checksum}</code>
                </div>
              </div>

              {selectedArtifact.tags && selectedArtifact.tags.length > 0 && (
                <div style={styles.drawerSection}>
                  <h4 style={styles.sectionTitle}>Tags</h4>
                  <div style={styles.tagsList}>
                    {selectedArtifact.tags.map(tag => (
                      <span key={tag} style={styles.tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedArtifact.metadata && Object.keys(selectedArtifact.metadata).length > 0 && (
                <div style={styles.drawerSection}>
                  <h4 style={styles.sectionTitle}>Metadata</h4>
                  <pre style={styles.metadata}>
                    {JSON.stringify(selectedArtifact.metadata, null, 2)}
                  </pre>
                </div>
              )}

              <div style={styles.drawerActions}>
                <button
                  onClick={() => handleDownload(selectedArtifact.id)}
                  disabled={downloading === selectedArtifact.id}
                  style={styles.primaryButton}
                >
                  {downloading === selectedArtifact.id ? 'Preparing...' : 'Download Artifact'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '150px' },
  filterInput: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', minWidth: '200px' },
  totalCount: { marginLeft: 'auto', fontSize: '0.875rem', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  nameCell: { display: 'flex', flexDirection: 'column', gap: '0.125rem' },
  artifactName: { fontWeight: '500', color: '#2d3748' },
  filename: { fontSize: '0.75rem', color: '#718096' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  downloadButton: { padding: '0.375rem 0.75rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  drawerOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  drawer: { width: '500px', maxWidth: '100%', background: 'white', height: '100%', overflow: 'auto', boxShadow: '-4px 0 6px rgba(0,0,0,0.1)' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  drawerTitle: { margin: 0, fontSize: '1.25rem', fontWeight: '600' },
  closeButton: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' },
  drawerContent: { padding: '1.5rem' },
  drawerSection: { marginBottom: '1.5rem' },
  drawerRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  drawerLabel: { color: '#718096', fontSize: '0.875rem' },
  sectionTitle: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  checksumBox: { padding: '0.75rem', background: '#f7fafc', borderRadius: '6px', overflow: 'auto' },
  tagsList: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  tag: { padding: '0.25rem 0.5rem', background: '#edf2f7', borderRadius: '4px', fontSize: '0.75rem', color: '#4a5568' },
  metadata: { margin: 0, padding: '1rem', background: '#f7fafc', borderRadius: '8px', fontSize: '0.75rem', overflow: 'auto' },
  drawerActions: { marginTop: '1.5rem' },
  primaryButton: { width: '100%', padding: '0.75rem', borderRadius: '6px', border: 'none', background: '#3182ce', color: 'white', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer' }
};
